import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GraduationCap, 
  ChevronRight, 
  ChevronLeft,
  Globe,
  Target,
  Clock,
  Heart,
  Sparkles,
  Loader2,
  Briefcase,
  TrendingUp,
  Users,
  Lightbulb
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles },
  { id: 'background', title: 'Background', icon: Globe },
  { id: 'goals', title: 'Goals', icon: Target },
  { id: 'time', title: 'Time', icon: Clock },
  { id: 'confidence', title: 'Confidence', icon: Heart },
  { id: 'finish', title: 'Finish', icon: GraduationCap }
];

const REGIONS = [
  { value: 'north_america', label: 'North America', emoji: '🌎' },
  { value: 'latin_america', label: 'Latin America', emoji: '🌎' },
  { value: 'europe', label: 'Europe', emoji: '🌍' },
  { value: 'africa', label: 'Africa', emoji: '🌍' },
  { value: 'middle_east', label: 'Middle East', emoji: '🌍' },
  { value: 'south_asia', label: 'South Asia', emoji: '🌏' },
  { value: 'east_asia', label: 'East Asia', emoji: '🌏' },
  { value: 'southeast_asia', label: 'Southeast Asia', emoji: '🌏' },
  { value: 'oceania', label: 'Oceania', emoji: '🌏' }
];

const EXPERIENCE_LEVELS = [
  { value: 'first_time', label: 'First-time founder', icon: Lightbulb },
  { value: 'attempted', label: 'Attempted before', icon: TrendingUp },
  { value: 'experienced', label: 'Experienced founder', icon: Briefcase },
  { value: 'serial', label: 'Serial entrepreneur', icon: Users }
];

const GOALS = [
  { value: 'validate_idea', label: 'Start a new venture', description: 'Validate and launch my idea' },
  { value: 'find_customers', label: 'Grow my business', description: 'Find customers and scale' },
  { value: 'raise_funding', label: 'Raise funding', description: 'Prepare for investors' },
  { value: 'build_confidence', label: 'Build confidence', description: 'Overcome imposter syndrome' }
];

const TIME_OPTIONS = [
  { value: 1, label: '1 hour/week', description: 'Quick daily sessions' },
  { value: 3, label: '3 hours/week', description: 'Steady progress' },
  { value: 5, label: '5+ hours/week', description: 'Deep dive learning' }
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    background: '',
    region: '',
    experience_level: 'first_time',
    goals: [],
    time_available: 3,
    confidence_level: 5,
    traction_stage: 'idea',
    preferred_language: 'en'
  });

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Convert goals array to primary goal
      const primaryGoal = data.goals.length > 0 ? data.goals[0] : 'validate_idea';
      const onboardingPayload = {
        experience_level: data.experience_level,
        founder_goal: primaryGoal,
        time_available: data.time_available,
        confidence_level: data.confidence_level,
        region: data.region,
        preferred_language: data.preferred_language
      };
      await api.completeOnboarding(onboardingPayload);
      updateUser({ onboarding_complete: true, ...onboardingPayload });
      
      // Route based on experience level:
      // First-time founders go directly to Lesson 1
      // Everyone else goes to dashboard to choose their own starting point
      if (data.experience_level === 'first_time') {
        toast.success('Welcome to Minnow School! Let\'s begin your first lesson.');
        navigate('/curriculum/module_0/lesson/0.1');
      } else {
        toast.success('Welcome back! Choose where to start your journey.');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return true;
      case 'background':
        return data.region && data.experience_level;
      case 'goals':
        return data.goals.length > 0;
      case 'time':
        return data.time_available > 0;
      case 'confidence':
        return true;
      case 'finish':
        return true;
      default:
        return false;
    }
  };

  const toggleGoal = (goal) => {
    setData(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal]
    }));
  };

  const renderStep = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-24 h-24 rounded-3xl gradient-ocean flex items-center justify-center mx-auto mb-8"
            >
              <GraduationCap className="w-12 h-12 text-white" />
            </motion.div>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Welcome to Minnow School, {user?.name?.split(' ')[0]}!
            </h2>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              Let's personalize your learning experience. This will only take a minute.
            </p>
          </div>
        );

      case 'background':
        return (
          <div className="space-y-8">
            <div className="space-y-4">
              <Label className="text-lg font-semibold">Where are you based?</Label>
              <div className="grid grid-cols-3 gap-3">
                {REGIONS.map((region) => (
                  <button
                    key={region.value}
                    type="button"
                    onClick={() => setData({ ...data, region: region.value })}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                      data.region === region.value 
                        ? 'border-ocean-blue bg-ocean-blue/5' 
                        : 'border-border hover:border-ocean-blue/50'
                    }`}
                    data-testid={`region-${region.value}`}
                  >
                    <span className="text-2xl mr-2">{region.emoji}</span>
                    <span className="font-medium text-sm">{region.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Your experience level?</Label>
              <div className="grid grid-cols-2 gap-3">
                {EXPERIENCE_LEVELS.map((exp) => (
                  <button
                    key={exp.value}
                    type="button"
                    onClick={() => setData({ ...data, experience_level: exp.value })}
                    className={`p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] flex items-center gap-3 ${
                      data.experience_level === exp.value 
                        ? 'border-ocean-blue bg-ocean-blue/5' 
                        : 'border-border hover:border-ocean-blue/50'
                    }`}
                    data-testid={`experience-${exp.value}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      data.experience_level === exp.value ? 'bg-ocean-blue text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      <exp.icon className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{exp.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'goals':
        return (
          <div className="space-y-6">
            <Label className="text-lg font-semibold">What brings you to Minnow School?</Label>
            <div className="grid gap-4">
              {GOALS.map((goal) => (
                <button
                  key={goal.value}
                  type="button"
                  onClick={() => toggleGoal(goal.value)}
                  className={`p-5 rounded-xl border-2 text-left transition-all hover:scale-[1.01] ${
                    data.goals.includes(goal.value) 
                      ? 'border-ocean-blue bg-ocean-blue/5' 
                      : 'border-border hover:border-ocean-blue/50'
                  }`}
                  data-testid={`goal-${goal.value}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">{goal.label}</h3>
                      <p className="text-sm text-muted-foreground">{goal.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      data.goals.includes(goal.value) 
                        ? 'border-ocean-blue bg-ocean-blue' 
                        : 'border-muted-foreground'
                    }`}>
                      {data.goals.includes(goal.value) && (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'time':
        return (
          <div className="space-y-6">
            <Label className="text-lg font-semibold">How much time can you dedicate?</Label>
            <div className="grid gap-4">
              {TIME_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setData({ ...data, time_available: option.value })}
                  className={`p-5 rounded-xl border-2 text-left transition-all hover:scale-[1.01] ${
                    data.time_available === option.value 
                      ? 'border-ocean-blue bg-ocean-blue/5' 
                      : 'border-border hover:border-ocean-blue/50'
                  }`}
                  data-testid={`time-${option.value}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">{option.label}</h3>
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 ${
                      data.time_available === option.value 
                        ? 'border-ocean-blue bg-ocean-blue' 
                        : 'border-muted-foreground'
                    }`}>
                      {data.time_available === option.value && (
                        <div className="w-full h-full rounded-full bg-ocean-blue flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'confidence':
        return (
          <div className="space-y-8">
            <div className="text-center">
              <Label className="text-lg font-semibold">How confident do you feel about your founder journey?</Label>
              <p className="text-sm text-muted-foreground mt-2">Be honest—this helps us personalize your experience</p>
            </div>
            
            <div className="space-y-8 pt-4">
              <div className="relative">
                <Slider
                  value={[data.confidence_level]}
                  onValueChange={([value]) => setData({ ...data, confidence_level: value })}
                  max={10}
                  min={1}
                  step={1}
                  className="py-4"
                  data-testid="confidence-slider"
                />
                <div className="flex justify-between text-sm text-muted-foreground mt-2">
                  <span>Just starting</span>
                  <span>Very confident</span>
                </div>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-ocean-blue/10">
                  <span className="text-5xl font-bold text-ocean-blue">{data.confidence_level}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'finish':
        return (
          <div className="text-center py-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-24 h-24 rounded-3xl bg-mint flex items-center justify-center mx-auto mb-8"
            >
              <Sparkles className="w-12 h-12 text-ocean-blue" />
            </motion.div>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              You're all set!
            </h2>
            <p className="text-lg text-muted-foreground max-w-md mx-auto mb-8">
              We've created a personalized learning path just for you. Ready to dive in?
            </p>
            <Card className="bg-sand-beige/30 border-0 rounded-2xl p-6 max-w-md mx-auto">
              <CardContent className="p-0">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl gradient-ocean flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-foreground">Your first module awaits</h3>
                    <p className="text-sm text-muted-foreground">Startup Jargon Cheat Sheet</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-between">
            <div className="w-24"></div>
            <div className="inline-flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl gradient-ocean flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-foreground">Minnow School</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                setLoading(true);
                try {
                  // Quick complete onboarding with defaults (first_time founder)
                  await api.completeOnboarding({
                    experience_level: 'first_time',
                    founder_goal: 'validate_idea',
                    time_available: 3,
                    confidence_level: 5,
                    region: 'north_america',
                    preferred_language: 'en'
                  });
                  updateUser({ onboarding_complete: true });
                  // First-time founders go to Lesson 1 for guided start
                  toast.success('Welcome to Minnow School! Let\'s begin your first lesson.');
                  navigate('/curriculum/module_0/lesson/0.1');
                } catch (error) {
                  toast.error('Something went wrong');
                } finally {
                  setLoading(false);
                }
              }}
              className="text-muted-foreground hover:text-foreground"
              data-testid="skip-onboarding-btn"
            >
              Skip
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center ${index < STEPS.length - 1 ? 'flex-1' : ''}`}
              >
                <div 
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                    index <= currentStep 
                      ? 'gradient-ocean text-white' 
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <step.icon className="w-5 h-5" />
                </div>
                {index < STEPS.length - 1 && (
                  <div 
                    className={`flex-1 h-1 mx-2 rounded-full transition-colors ${
                      index < currentStep ? 'bg-ocean-blue' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <Card className="rounded-2xl shadow-lg border-0 p-6 sm:p-8" data-testid="onboarding-card">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {/* Validation Hint */}
          {!canProceed() && currentStep !== 0 && currentStep !== STEPS.length - 1 && (
            <p className="text-sm text-muted-foreground text-center mt-4 bg-muted/50 p-3 rounded-xl" data-testid="validation-hint">
              {STEPS[currentStep].id === 'background' && !data.region && 'Please select your region'}
              {STEPS[currentStep].id === 'goals' && data.goals.length === 0 && 'Please select at least one goal'}
            </p>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="rounded-full"
              data-testid="onboarding-back-btn"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className="rounded-full gradient-ocean text-white"
              data-testid="onboarding-next-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up...
                </>
              ) : currentStep === STEPS.length - 1 ? (
                <>
                  Enter Minnow School
                  <Sparkles className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingPage;
